import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";

declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
  }
}

export default function TawkToChat() {
  const { user } = useAuth();

  useEffect(() => {
    // Check if Tawk.to is already loaded
    if (document.querySelector('script[src*="embed.tawk.to"]')) {
      console.log("Tawk.to script already exists");
      return;
    }

    // Add custom CSS immediately to prevent chat widget from covering back to top button
    const addCustomChatStyles = () => {
      const existingStyle = document.getElementById("tawk-custom-styles");
      if (!existingStyle) {
        const style = document.createElement("style");
        style.id = "tawk-custom-styles";
        style.textContent = `
          /* Move Tawk.to chat widget to the left to avoid back to top button */
          div[id*="tawk"], 
          iframe[id*="tawk"],
          #tawk-widget-embedded,
          .tawk-min-container,
          .tawk-tooltip,
          div[data-tawk*="widget"] {
            right: 140px !important;
            bottom: 24px !important;
            z-index: 40 !important;
          }
          
          /* Target all possible Tawk widget containers */
          div[style*="position: fixed"][style*="right:"][style*="bottom:"] {
            right: 140px !important;
            z-index: 40 !important;
          }
          
          /* More specific targeting for Tawk elements */
          iframe[src*="tawk.to"] {
            right: 140px !important;
            z-index: 40 !important;
          }
          
          /* Ensure back to top button stays visible and positioned correctly */
          button[aria-label="Back to top"] {
            position: fixed !important;
            bottom: 24px !important;
            right: 24px !important;
            z-index: 60 !important;
          }
          
          /* Additional targeting for any Tawk containers */
          div[id^="tawk_"] {
            right: 140px !important;
            z-index: 40 !important;
          }
        `;
        document.head.appendChild(style);
        console.log("✅ Chat positioning styles added");
      }
    };

    // Apply styles immediately
    addCustomChatStyles();

    // Set the load start time
    window.Tawk_LoadStart = new Date();

    // Initialize Tawk_API
    window.Tawk_API = window.Tawk_API || {};

    // Configure Tawk.to callbacks
    window.Tawk_API.onLoad = function () {
      console.log("✅ Tawk.to chat loaded successfully");

      // Re-apply custom positioning styles after chat loads
      setTimeout(addCustomChatStyles, 100);
      setTimeout(addCustomChatStyles, 500);
      setTimeout(addCustomChatStyles, 1000);

      // Set user information if logged in
      if (user && window.Tawk_API) {
        const userName =
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          user.email?.split("@")[0] ||
          "MealBridge User";

        try {
          // Method 1: Set visitor attributes (recommended)
          window.Tawk_API.setAttributes(
            {
              name: userName,
              email: user.email || "",
              userId: user.id || "",
              userType: user.user_metadata?.role || "user",
              signUpDate: user.created_at || "",
            },
            function (error: any) {
              if (error) {
                console.error("❌ Error setting Tawk.to attributes:", error);
              } else {
                console.log("✅ Tawk.to user attributes set successfully", {
                  name: userName,
                  email: user.email,
                  userId: user.id,
                });
              }
            }
          );

          // Method 2: Add visitor tags for easier identification
          if (window.Tawk_API.addTags) {
            const tags = ["registered-user"];
            if (user.user_metadata?.role) {
              tags.push(user.user_metadata.role);
            }
            window.Tawk_API.addTags(tags, function (error: any) {
              if (error) {
                console.error("❌ Error adding Tawk.to tags:", error);
              } else {
                console.log("✅ Tawk.to tags added:", tags);
              }
            });
          }

          // Method 3: Set secure hash for verified users (optional)
          if (window.Tawk_API.setSecureMode && user.id) {
            // Note: In production, you should generate this hash on your backend
            // For now, we'll just log that secure mode could be enabled
            console.log("🔐 Secure mode available for user:", user.id);
          }
        } catch (error) {
          console.error("❌ Error in setAttributes:", error);
        }
      } else if (!user && window.Tawk_API) {
        // Handle anonymous users
        try {
          window.Tawk_API.setAttributes({
            name: "Anonymous Visitor",
            email: "",
            userId: "anonymous-" + Date.now(),
          });
          console.log("✅ Anonymous user attributes set");
        } catch (error) {
          console.error("❌ Error setting anonymous attributes:", error);
        }
      }

      // Ensure the widget is visible
      if (window.Tawk_API.showWidget) {
        window.Tawk_API.showWidget();
      }
    };

    // Enhanced chat event handlers
    window.Tawk_API.onChatStarted = function () {
      console.log("💬 Chat started");

      // Re-send user info when chat starts to ensure it's captured
      if (user && window.Tawk_API) {
        const userName =
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          user.email?.split("@")[0] ||
          "MealBridge User";

        window.Tawk_API.setAttributes({
          name: userName,
          email: user.email || "",
          userId: user.id || "",
        });
        console.log("🔄 User info re-sent on chat start");
      }
    };

    window.Tawk_API.onChatEnded = function () {
      console.log("💬 Chat ended");
    };

    // Create and load the script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://embed.tawk.to/68c078a7a71a6d1925755a8f/1j4o09qdc";
    script.charset = "UTF-8";
    script.setAttribute("crossorigin", "*");

    script.onload = () => {
      console.log("✅ Tawk.to script loaded");
    };

    script.onerror = (error) => {
      console.error("❌ Failed to load Tawk.to script:", error);
    };

    // Add to document
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    console.log("🚀 Initializing Tawk.to chat widget...");

    // Cleanup function
    return () => {
      // Remove custom styles when component unmounts
      const customStyle = document.getElementById("tawk-custom-styles");
      if (customStyle) {
        customStyle.remove();
      }
    };
  }, []);

  // Update user info when user changes (separate useEffect)
  useEffect(() => {
    if (window.Tawk_API && user) {
      const userName =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.email?.split("@")[0] ||
        "MealBridge User";

      try {
        window.Tawk_API.setAttributes(
          {
            name: userName,
            email: user.email || "",
            userId: user.id || "",
            userType: user.user_metadata?.role || "user",
            signUpDate: user.created_at || "",
          },
          function (error: any) {
            if (error) {
              console.error("❌ Error updating Tawk.to user info:", error);
            } else {
              console.log("✅ Updated Tawk.to user info:", {
                name: userName,
                email: user.email,
                userId: user.id,
              });
            }
          }
        );

        // Add user tags for better identification
        if (window.Tawk_API.addTags) {
          const tags = ["registered-user"];
          if (user.user_metadata?.role) {
            tags.push(user.user_metadata.role);
          }
          window.Tawk_API.addTags(tags);
        }
      } catch (error) {
        console.error("❌ Error updating Tawk.to user info:", error);
      }
    } else if (window.Tawk_API && !user) {
      // Handle user logout - reset to anonymous
      try {
        // Check if setAttributes method exists before calling it
        if (typeof window.Tawk_API.setAttributes === "function") {
          window.Tawk_API.setAttributes({
            name: "Anonymous Visitor",
            email: "",
            userId: "anonymous-" + Date.now(),
          });
          console.log("✅ Reset to anonymous user");
        } else {
          console.warn("⚠️ Tawk_API.setAttributes not available");
        }
      } catch (error) {
        console.error("❌ Error resetting to anonymous:", error);
      }
    }
  }, [user]);

  return null; // This component doesn't render anything
}
